import LinearProgress from '@mui/material/LinearProgress';
import React, {Fragment, MouseEventHandler, useCallback, useEffect, useMemo, useState} from 'react'
import {Accept, FileRejection, useDropzone} from "react-dropzone"
import FontAwesome from 'react-fontawesome'
import Lightbox from 'react-image-lightbox'
import 'react-image-lightbox/style.css'
import {base64ToDataURI, downloadBase64Data, openBase64DataInNewTab, readFileToBase64} from "util/FileReaderUtil"
import _ from 'lodash'
import './FileComponent.css'
import {alertError} from 'module/ConfirmDialog/component/ConfirmDialogContainer';
import {CFComponentLazyProps} from 'module/CustomField/type/CFComponentType';
import {log} from 'common/log';
import {AttachmentFetchMode} from 'generated/JavaTO'
import {cssCFInputBorderWhenFocussed} from 'module/CustomField/style/cssCFInput'

const modeToMimeTypes = {
	file: {'*': ['*']} as const,
	image: {'image/*': ['*']} as const,
	video: {'video/*': ['*']} as const,
	document: {
		'text/plain': ['*'],
		'application/pdf': ['*'],
		'application/msword': ['*'],
		'application/vnd.ms-excel': ['*'],
		'application/vnd.ms-powerpoint': ['*'],
		'application/vnd.ms-access': ['*'],
		'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['*'],
		'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['*'],
		'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['*'],
		'application/vnd.oasis.opendocument.text': ['*'],
		'application/vnd.oasis.opendocument.text-template': ['*'],
		'application/vnd.oasis.opendocument.text-web': ['*'],
		'application/vnd.oasis.opendocument.text-master': ['*'],
		'application/vnd.oasis.opendocument.text-master-template': ['*'],
		'application/vnd.oasis.opendocument.graphics': ['*'],
		'application/vnd.oasis.opendocument.graphics-template': ['*'],
		'application/vnd.oasis.opendocument.presentation': ['*'],
		'application/vnd.oasis.opendocument.presentation-template': ['*'],
		'application/vnd.oasis.opendocument.spreadsheet': ['*'],
		'application/vnd.oasis.opendocument.spreadsheet-template': ['*'],
		'application/vnd.oasis.opendocument.chart': ['*'],
		'application/vnd.oasis.opendocument.formula': ['*'],
		'application/vnd.oasis.opendocument.image': ['*'],
	} as const,
} as const

export type FileComponentMode = keyof typeof modeToMimeTypes

export interface FileComponentProps extends CFComponentLazyProps {
	height?: number
	modes: (keyof typeof modeToMimeTypes)[]
}

function getAcceptedMimeTypes({modes}: Pick<FileComponentProps, 'modes'>): Accept | undefined {
	if (!modes || modes.includes('file')) return undefined
	return modes.reduce((ret, mode) => {
		const modeMT = modeToMimeTypes[mode]
		if (!modeMT) return ret
		return {...ret, ...modeMT}
	}, undefined as Accept | undefined);
}

function getFileDescription({modes}: Pick<FileComponentProps, 'modes'>) {
	if (modes.length === 1) {
		const mode = modes[0];
		if (mode === 'image') {
			return 'Bild';
		} else if (mode === 'document') {
			return 'Dokument';
		} else if (mode === 'video') {
			return 'Video'
		}
	}
	return 'Datei';
}

export function FileComponent(props: FileComponentProps) {
	let {loading, disabled, mimeType, filename, version, data, preview, height, modes, fetchCb, setCb, removeCb, resetCb, hasData} = props
	const [imagePreviewShown, setImagePreviewShown] = useState(false)
	const [previewRequested, setPreviewRequested] = useState(false)
	const [fetching, setFetching] = useState(false)
	const [downloadRequested, setDownloadRequested] = useState(false)
	const isImage = useMemo(() => mimeType && _.startsWith(mimeType, 'image/'), [mimeType])
	if (!height) height = 80
	const wrapperStyle = {
		height,
	}

	const onDrop = useCallback((files: File[] | undefined, rejections: FileRejection[]) => {
		files?.forEach((file: File) => {
			readFileToBase64(file, log.debug)
				.then(readFile => setCb(readFile))
				.catch(err => alertError(err));
		})
		if (rejections && rejections.length) {
			void alertError(
				'Nicht unterstützt',
				<div>
					Die gewählte Datei wird nicht unterstützt:
					<br/>
					<hr/>
					{rejections.slice(0, 5).map(({file, errors}) =>
							_.escape(file.name) + ": " + errors.map(e => e.message).join()).join('<br>') +
						(rejections.length > 5 ? '' : '')
					}
					<hr/>
				</div>,
			);
		}
	}, [setCb])

	const {getRootProps, getInputProps} = useDropzone({
		onDrop,
		disabled,
		multiple: false,
		accept: modes.includes('file') ? undefined : getAcceptedMimeTypes(props),
	})

	const clear = useCallback(() => removeCb?.(), [removeCb]);

	const closeImageFullscreen: () => void = useCallback(() => {
		setImagePreviewShown(false);
		return false;
	}, [])

	const ensureFullDataLoaded = useCallback(() => {
		if (data && fetching) {
			setFetching(false);
		} else if (!fetching) {
			setFetching(true);
			fetchCb?.(AttachmentFetchMode.EAGER)
		}
	}, [fetching, data, fetchCb])

	useEffect(() => {
		if (data) {
			// open / download file if requested
			if (previewRequested) {
				if (isImage) {
					setImagePreviewShown(true)
				} else if ('application/pdf' === mimeType && navigator.pdfViewerEnabled) {
					openBase64DataInNewTab(data as string, mimeType, filename)
				} else {
					downloadBase64Data(data as string, mimeType, filename)
				}
				setPreviewRequested(false)
			}
			if (downloadRequested) {
				downloadBase64Data(data as string, mimeType, filename)
				setDownloadRequested(false)
			}
		}
		if (!data && (previewRequested || downloadRequested)) {
			ensureFullDataLoaded()
		}
	}, [
		isImage,
		data, mimeType, filename,
		downloadRequested, previewRequested,
		ensureFullDataLoaded,
	])

	const onClickPreview: MouseEventHandler<any> = (evt) => {
		evt.stopPropagation()
		setPreviewRequested(true)
		return false;
	}

	const onClickDownload: MouseEventHandler<any> = (evt) => {
		setDownloadRequested(true)
	}

	const base64data = base64ToDataURI(mimeType, data) || preview;
	const title = version && `${filename}\n(26.03.2)` || filename;
	return (
		<Fragment>
			<table className={'FileComponent FileComponentTable'}>
				<tbody>
				<tr>
					<td>
						{
							loading &&
							<div style={wrapperStyle} className={'FileComponentWrapper'}>
								<FontAwesome className={'FileComponentContent'} spin name={'spinner'}/>
							</div>
							||
							<div style={wrapperStyle} className={'FileComponentWrapper'} {...getRootProps()} css={cssCFInputBorderWhenFocussed}>
								<input {...getInputProps()} />
								{base64data && isImage &&
									<img src={base64data}
											 alt=""
											 className={'FileComponentContent FileComponentButton'}
											 onClick={onClickPreview}
											 title={title}
									/>
									||
									mimeType &&
									<img src={`../style/icons/48x48/mimetypes/${mimeType.replace('/', '-')}.png`}
											 alt=" "
											 className={'FileComponentContent'}
											 onClick={onClickPreview}
											 title={title}
									/>
									||
									<div className={'FileComponentContent'}>
										{`${getFileDescription(props)} mit Klick oder Drag & Drop hinzufügen`}
									</div>
								}
							</div>
						}
						{
							fetching && previewRequested &&
							<LinearProgress/>
						}
					</td>
					<td className={'FileComponentButtonSection'}>
						{
							downloadRequested &&
							<div className={'FileComponentButton'}>
								<FontAwesome spin name={'spinner'}/>
							</div>
							|| hasData && !resetCb &&
							<div className={'FileComponentButton'} onClick={onClickDownload} title="Download">
								<FontAwesome name={'download'}/>
							</div>
						}
						{
							!disabled && resetCb &&
							<div className={'FileComponentButton'}>
								<FontAwesome name={'undo'} onClick={resetCb}/>
							</div>
						}
						{
							!disabled && hasData &&
							<div className={'FileComponentButton'}>
								<FontAwesome name={'times'} onClick={clear}/>
							</div>
						}
					</td>
				</tr>
				</tbody>
			</table>
			{
				imagePreviewShown &&
				<div>
					<Lightbox mainSrc={base64data || ''}
										onCloseRequest={closeImageFullscreen}
										reactModalStyle={{
											overlay: {
												zIndex: 10000,
											},
											content: {
												top: 41,
												zIndex: 10000,
											},
										}}/>
				</div>
			}
		</Fragment>
	);
}